import { all, call, put, select, takeEvery } from 'typed-redux-saga';

import { logError } from 'src/loggingManager';
import { RELOAD } from 'src/store/actions/actionTypes';
import { RootState } from 'src/store';
import { getSkuData, getSurfaceProperties } from 'src/selectors/pages/editor';
import { retrieveDesignData, RetrieveDesignDataParams } from 'src/services/disClient';
import { fetchProductModel } from 'src/services/productModelClient';
import { resolveEffectiveSku } from 'src/util/skuKey';
import type { DesignDataResultType } from 'src/types/decorationSpecs';
import type { GenericSelectorProductType } from 'src/types/product';
import { applyVariables, changeEditorSkuSuccess } from 'src/pages/editor/components/surfaces/slice';
import {
    retrieveDecorationSpecs,
    retrieveDecorationSpecsSettled,
    requestProductModel,
    productModelResolved,
} from './slice';
import { unresolvedProductOptions } from './unresolvedProductOptions';

const getCacheKey = (state: RootState) => state.pages.editor.decorationSpecs.cacheKey;
const getProductModelKey = (state: RootState) => state.pages.editor.decorationSpecs.productModelKey;

function buildCacheKey({
    sku,
    skuVersion,
    merchantSku,
    merchantVersion,
    merchantVariables,
}: RetrieveDesignDataParams): string {
    return `${sku}-${skuVersion}-${merchantSku}-${merchantVersion}-${JSON.stringify(merchantVariables ?? {})}`;
}

/** Resolves the DIS fetch's result, converting a real (non-404/400) error into a message rather
 * than letting an uncaught rejection abort `fetchDecorationSpecs` entirely. */
function* fetchDesignDataSafely(
    params: RetrieveDesignDataParams,
): Generator<any, { data?: DesignDataResultType; error?: string }, any> {
    try {
        const data = yield* call(retrieveDesignData, params);
        return { data };
    } catch (error: any) {
        logError(`Failed to fetch DIS design data for decoration specs panel: ${error}`);
        return { error: (error && error.message) || 'Failed to fetch decoration specs' };
    }
}

/** Short-circuits `fetchDesignDataSafely` when `unresolvedProductOptions` already found the
 * current selection incomplete — skips the network call entirely rather than trusting whatever
 * DIS would otherwise silently answer (see `unresolvedProductOptions.ts` for why that answer
 * can't be trusted). Not a generator — `call()` accepts a plain function just as well, and this
 * one has no async work or yields to perform. */
function skipDesignData(missingSelections: string[]): { data: DesignDataResultType } {
    return { data: { decorationPlacement: null, printWhite: null, missingSelections } };
}

/**
 * Fetches the full product model definition (options and property rules/assignments) from api.products.cimpress.io
 * if it hasn't been fetched for the current effective SKU and version yet.
 */
export function* fetchProductModelIfNeeded(): Generator<any, void, any> {
    const { sku, skuVersion, merchantSku, merchantVersion } = yield* select(getSurfaceProperties);
    const { effectiveSku, effectiveVersion } = resolveEffectiveSku({
        sku,
        skuVersion,
        merchantSku,
        merchantVersion,
    });

    if (!effectiveSku) {
        return;
    }

    const modelKey = effectiveVersion !== undefined ? `${effectiveSku}-${effectiveVersion}` : effectiveSku;
    const currentModelKey = yield* select(getProductModelKey);
    if (currentModelKey === modelKey) {
        // Already fetched (or already in flight, since `requestProductModel` below sets this
        // optimistically before the request resolves) for this exact product/version.
        return;
    }

    // Set optimistically before the request resolves, so a slower/superseded response can be
    // told apart from the current one in `productModelResolved` (see that reducer's comment).
    yield* put(requestProductModel({ productModelKey: modelKey }));

    try {
        const productModel: GenericSelectorProductType | null = yield* call(fetchProductModel, {
            productId: effectiveSku,
            version: effectiveVersion,
        });
        yield* put(productModelResolved({ productModelKey: modelKey, productModel }));
    } catch (error: any) {
        logError(`Failed to fetch product model for ${modelKey}: ${error}`);
        yield* put(productModelResolved({ productModelKey: modelKey, productModel: null }));
    }
}

/**
 * Fetches decoration-placement/print-white data from DIS for the current product/selection.
 */
export function* fetchDecorationSpecs(): Generator<any, void, any> {
    const { sku, skuVersion, merchantSku, merchantVersion, merchantVariables } = yield* select(getSurfaceProperties);

    // Nothing to look up yet (no product chosen).
    if (!sku && !merchantSku) {
        return;
    }

    const cacheKey = buildCacheKey({ sku, skuVersion, merchantSku, merchantVersion, merchantVariables });
    const currentCacheKey = yield* select(getCacheKey);
    if (currentCacheKey === cacheKey) {
        // Product identity and every merchant variable are unchanged since the last fetch for this
        // selection — nothing new to look up. Refetching on every unrelated action (e.g. a surface
        // page switch) would be a wasted DIS call.
        return;
    }

    yield* put(retrieveDecorationSpecs({ cacheKey }));

    // Proactively check completeness against this product's own declared options, rather than
    // solely trusting DIS's response to tell us the selection is ambiguous — see
    // unresolvedProductOptions.ts. Decoration placement/print-white can vary on any of a product's
    // options (Substrate Color, Backside, Decoration Technology, ...).
    const skuData = yield* select(getSkuData);
    // `SurfaceStateType.options` is typed as `Record<string, any>` but the real API response (and
    // this app's own storage of it) is an array of `{ name, ... }` entries — see
    // unresolvedProductOptions.ts's own comment on this.
    const productOptions = skuData?.options as { name?: string }[] | undefined;
    const unresolvedForDecoration = unresolvedProductOptions(productOptions, merchantVariables);

    const designResult = (yield* unresolvedForDecoration.length > 0
        ? call(skipDesignData, unresolvedForDecoration)
        : call(fetchDesignDataSafely, { sku, skuVersion, merchantSku, merchantVersion, merchantVariables })) as {
        data?: DesignDataResultType;
        error?: string;
    };

    yield* put(
        retrieveDecorationSpecsSettled({
            cacheKey,
            decorationPlacement: designResult.data?.decorationPlacement ?? null,
            printWhite: designResult.data?.printWhite ?? null,
            decorationMissingSelections: designResult.data?.missingSelections ?? null,
            error: designResult.error,
        }),
    );
}

export default function* decorationSpecsSaga() {
    return yield* all([
        yield* takeEvery(applyVariables, fetchDecorationSpecs),
        yield* takeEvery(changeEditorSkuSuccess, function* () {
            yield* call(fetchProductModelIfNeeded);
            yield* call(fetchDecorationSpecs);
        }),
        yield* takeEvery(RELOAD, function* () {
            yield* call(fetchProductModelIfNeeded);
            yield* call(fetchDecorationSpecs);
        }),
    ]);
}
